import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { ShieldCheck, AlertTriangle, CheckCircle, XCircle, RefreshCw, Loader2, Wrench, FileText, Copy, ExternalLink, Rss } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const scoreColor = (s) => s >= 90 ? '#16a34a' : s >= 70 ? '#ca8a04' : '#dc2626';

function FeedURLsSection() {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const xmlUrl = `${baseUrl}/api/feed.xml`;
  const csvUrl = `${baseUrl}/api/feed.csv`;

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  return (
    <div className="rounded-lg border p-5 mb-6" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
      <div className="flex items-center gap-2 mb-4">
        <Rss size={16} style={{ color: '#1A1A1A' }} />
        <h3 className="font-sans text-sm font-bold" style={{ color: '#1A1A1A' }}>Feed URLs</h3>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="font-sans text-xs font-medium w-12" style={{ color: '#6B7280' }}>XML</span>
          <div className="flex-1 px-3 py-2 rounded font-mono text-xs truncate" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>{xmlUrl}</div>
          <button data-testid="copy-xml-url" onClick={() => copy(xmlUrl)} className="p-2 rounded hover:bg-gray-100 transition-colors"><Copy size={14} style={{ color: '#6B7280' }} /></button>
          <a href={xmlUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-gray-100 transition-colors"><ExternalLink size={14} style={{ color: '#6B7280' }} /></a>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-sans text-xs font-medium w-12" style={{ color: '#6B7280' }}>CSV</span>
          <div className="flex-1 px-3 py-2 rounded font-mono text-xs truncate" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>{csvUrl}</div>
          <button data-testid="copy-csv-url" onClick={() => copy(csvUrl)} className="p-2 rounded hover:bg-gray-100 transition-colors"><Copy size={14} style={{ color: '#6B7280' }} /></button>
          <a href={csvUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-gray-100 transition-colors"><ExternalLink size={14} style={{ color: '#6B7280' }} /></a>
        </div>
      </div>
      <p className="font-sans text-[10px] mt-3" style={{ color: '#9CA3AF' }}>Submit these URLs to Google Merchant Center to list your products on Google Shopping.</p>
    </div>
  );
}

function FeedAuditTab() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const runAudit = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/feed/audit'); setAudit(data); }
    catch { toast.error('Audit failed'); }
    setLoading(false);
  }, []);

  useEffect(() => { runAudit(); }, [runAudit]);

  const autoFix = async () => {
    setFixing(true);
    try {
      const { data } = await api.post('/feed/fix-all');
      toast.success(`Fixed ${data.fixed} products`);
      runAudit();
    } catch { toast.error('Fix failed'); }
    setFixing(false);
  };

  if (loading) return <div className="p-12 text-center font-sans text-sm" style={{ color: '#9CA3AF' }}>Running audit...</div>;

  const s = audit?.summary;

  return (
    <div className="space-y-6">
      <FeedURLsSection />

      {/* Score */}
      <div className="flex items-center gap-6 p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
        <div className="text-center">
          <p className="font-sans text-4xl font-bold" style={{ color: scoreColor(s?.score || 0) }}>{s?.score || 0}</p>
          <p className="font-sans text-xs uppercase tracking-wider mt-1" style={{ color: '#9CA3AF' }}>Health Score</p>
        </div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'In Feed', value: s?.in_feed || 0, c: '#16a34a' },
            { label: 'Excluded', value: s?.excluded || 0, c: '#6b7280' },
            { label: 'Errors', value: s?.errors || 0, c: '#dc2626' },
            { label: 'Warnings', value: s?.warnings || 0, c: '#ca8a04' },
          ].map(({ label, value, c }) => (
            <div key={label}><p className="font-sans text-lg font-medium" style={{ color: c }}>{value}</p><p className="font-sans text-xs" style={{ color: '#9CA3AF' }}>{label}</p></div>
          ))}
        </div>
        <div className="flex gap-2">
          {s?.auto_fixable_count > 0 && (
            <Button data-testid="auto-fix-btn" onClick={autoFix} disabled={fixing} className="rounded-lg font-sans text-xs gap-1" style={{ backgroundColor: '#000', color: '#fff' }}>
              {fixing ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />} Auto-Fix ({s.auto_fixable_count})
            </Button>
          )}
          <Button variant="outline" onClick={runAudit} className="rounded-lg font-sans text-xs gap-1" style={{ borderColor: '#D1D5DB' }}>
            <RefreshCw size={13} /> Re-Audit
          </Button>
        </div>
      </div>

      {/* Products table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
        <table className="w-full">
          <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
            {['Status', 'Product', 'Errors', 'Warnings', 'Details'].map(h => (
              <th key={h} className="text-left p-3 font-sans text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {(audit?.products || []).map(p => {
              const sc = { approved: '#16a34a', warning: '#ca8a04', error: '#dc2626', excluded: '#6b7280' };
              return (
                <tr key={p.handle} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                  <td className="p-3">
                    {p.status === 'approved' ? <CheckCircle size={15} style={{ color: sc.approved }} /> :
                     p.status === 'warning' ? <AlertTriangle size={15} style={{ color: sc.warning }} /> :
                     p.status === 'error' ? <XCircle size={15} style={{ color: sc.error }} /> :
                     <span className="font-sans text-xs" style={{ color: sc.excluded }}>Excluded</span>}
                  </td>
                  <td className="p-3 font-sans text-sm" style={{ color: '#1A1A1A' }}>{p.title || p.handle}</td>
                  <td className="p-3"><Badge className="rounded-sm text-[10px]" style={{ backgroundColor: p.errors.length ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', color: p.errors.length ? '#dc2626' : '#16a34a', border: 'none' }}>{p.errors.length}</Badge></td>
                  <td className="p-3"><Badge className="rounded-sm text-[10px]" style={{ backgroundColor: p.warnings.length ? 'rgba(234,179,8,0.1)' : 'rgba(34,197,94,0.1)', color: p.warnings.length ? '#ca8a04' : '#16a34a', border: 'none' }}>{p.warnings.length}</Badge></td>
                  <td className="p-3">
                    <div className="space-y-0.5">
                      {p.errors.slice(0, 2).map((e, i) => <p key={i} className="font-sans text-[10px]" style={{ color: '#dc2626' }}>L{e.layer}: {e.message}</p>)}
                      {p.warnings.slice(0, 2).map((w, i) => <p key={i} className="font-sans text-[10px]" style={{ color: '#ca8a04' }}>L{w.layer}: {w.message}</p>)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComplianceTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/compliance/check').then(r => setReport(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center font-sans text-sm" style={{ color: '#9CA3AF' }}>Checking compliance...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
        <div className="text-center">
          <p className="font-sans text-4xl font-bold" style={{ color: scoreColor(report?.overall_score || 0) }}>{report?.overall_score || 0}%</p>
          <p className="font-sans text-xs uppercase tracking-wider mt-1" style={{ color: '#9CA3AF' }}>Compliance Score</p>
        </div>
        <Badge className="rounded-sm text-xs uppercase" style={{
          backgroundColor: report?.status === 'compliant' ? 'rgba(34,197,94,0.1)' : report?.status === 'at_risk' ? 'rgba(234,179,8,0.1)' : 'rgba(239,68,68,0.1)',
          color: report?.status === 'compliant' ? '#16a34a' : report?.status === 'at_risk' ? '#ca8a04' : '#dc2626',
          border: 'none'
        }}>{report?.status?.replace(/_/g, ' ')}</Badge>
      </div>

      {report?.critical_issues?.length > 0 && (
        <div className="p-4 rounded-lg border" style={{ borderColor: 'rgba(239,68,68,0.2)', backgroundColor: 'rgba(239,68,68,0.03)' }}>
          <p className="font-sans text-xs uppercase tracking-wider font-medium mb-2" style={{ color: '#dc2626' }}>Critical Issues</p>
          {report.critical_issues.map((i, idx) => <p key={idx} className="font-sans text-sm" style={{ color: '#dc2626' }}>&bull; {i}</p>)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(report?.categories || {}).map(([key, cat]) => (
          <div key={key} className="p-5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-sans text-sm font-medium capitalize" style={{ color: '#1A1A1A' }}>{key.replace(/_/g, ' ')}</h3>
              <span className="font-sans text-sm font-medium" style={{ color: scoreColor(cat.score) }}>{cat.score}%</span>
            </div>
            <div className="space-y-1.5">
              {cat.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  {item.passed ? <CheckCircle size={13} style={{ color: '#16a34a' }} /> : <XCircle size={13} style={{ color: '#dc2626' }} />}
                  <span className="font-sans text-xs" style={{ color: item.passed ? '#6B7280' : '#dc2626' }}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FeedCompliancePage() {
  return (
    <motion.div data-testid="feed-compliance-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-8">
        <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Feed & Compliance</h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#6B7280' }}>Google Shopping feed audit and store compliance</p>
      </div>

      <Tabs defaultValue="feed">
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start gap-0 h-auto p-0" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
          <TabsTrigger value="feed" data-testid="tab-feed-audit" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#000] data-[state=active]:text-[#1A1A1A] font-sans text-sm px-5 py-3 gap-2">
            <FileText size={15} /> Feed Audit
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#000] data-[state=active]:text-[#1A1A1A] font-sans text-sm px-5 py-3 gap-2">
            <ShieldCheck size={15} /> Compliance
          </TabsTrigger>
        </TabsList>
        <TabsContent value="feed" className="pt-6"><FeedAuditTab /></TabsContent>
        <TabsContent value="compliance" className="pt-6"><ComplianceTab /></TabsContent>
      </Tabs>
    </motion.div>
  );
}
